import { useCallback, useEffect, useMemo, useState } from 'react';

import type { PipelineBoard, PipelineDragMove } from '@/pages/pipelines/common/types';

import { applyPipelineCardMove } from '../utils';

type PendingCardMove = { move: PipelineDragMove; movedAt: string };

/**
 * Overlays not-yet-confirmed drops on top of the server board so a moved card
 * appears in its new column immediately (and the drop can animate into place)
 * instead of snapping back while the request is in flight.
 *
 * `addOptimisticMove` applies a move and returns a function that reverts it —
 * call that on a failed request.
 */
export const usePipelineOptimisticBoard = (board: PipelineBoard) => {
  const [pendingMoves, setPendingMoves] = useState<PendingCardMove[]>([]);

  const optimisticBoard = useMemo(
    () => pendingMoves.reduce((result, pending) => applyPipelineCardMove(result, pending), board),
    [board, pendingMoves],
  );

  // Once the server board reflects a move (the card has left its source column),
  // stop re-applying it locally so the optimistic overlay hands off seamlessly.
  useEffect(() => {
    const stageByCardId = new Map<string, string>();
    for (const column of board.columns) {
      for (const card of column.cards) stageByCardId.set(card.id, column.stage);
    }

    setPendingMoves((current) => {
      // Keep a move only while the server still shows the card in its source
      // column; once it has actually moved there, the optimistic copy is redundant.
      const next = current.filter(
        (pending) => stageByCardId.get(pending.move.cardId) === pending.move.sourceStage,
      );
      return next.length === current.length ? current : next;
    });
  }, [board]);

  const addOptimisticMove = useCallback((move: PipelineDragMove) => {
    const pending: PendingCardMove = { move, movedAt: new Date().toISOString() };
    setPendingMoves((current) => [...current, pending]);
    return () => setPendingMoves((current) => current.filter((entry) => entry !== pending));
  }, []);

  return { optimisticBoard, addOptimisticMove };
};
